import { TaskSubTicketStatus } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources/TaskSubTicketResource';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { useStore } from 'effector-react';
import React, { HTMLAttributes, useEffect, useRef } from 'react';

import Avatar from '@app/components/Avatar';
import RouterLink from '@app/components/RouterLink';
import DevText from '@app/components/Text';
import { Endpoints, imageHashPreview, taskSubTicketStatusInformation } from '@app/data/consts';
import { Card, Colors, Tag } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import { $permissions } from '@containers/store/states';
import { $task } from '@pages/Task/store/states';

import { fetchSubTicketSummary } from '../../../../pages/TaskSubTickets/store/effects';
import { resetSubTicketSummary } from '../../../../pages/TaskSubTickets/store/events';
import {
    claimScope,
    invalidateScope,
    nextGeneration,
    releaseScope,
    summaryKey,
} from '../../../../pages/TaskSubTickets/store/requestGeneration';
import { $subTicketSummary } from '../../../../pages/TaskSubTickets/store/states';
import {
    formatLoggedMinutes,
    getSubTicketStatusTagStyle,
    resolveAssigneeAssignments,
} from '../../../../pages/TaskSubTickets/utils';

type Props = HTMLAttributes<HTMLDivElement>;

// Ordered as the phase actually progresses, so the aside reads like a pipeline rather than an
// alphabetical dump. All four segments always render, including the empty ones — a zero is
// information here, and a summary whose shape changes with the data is hard to scan.
const STATUS_ORDER: TaskSubTicketStatus[] = ['to_design', 'in_progress', 'in_revision', 'complete'];

const SubTicketSummary: React.FC<Props> = (props) => {
    const permissions = useStore($permissions);
    const task = useStore($task);
    const summary = useStore($subTicketSummary);

    const isModuleEnabled = permissions.isEnabled.taskSubTicket || permissions.isRoot.taskSubTicket;

    // Exactly one summary request per mounted task. The aside renders on every Request tab and
    // re-renders on every parent store change, so without this guard a tab switch or a
    // participant update would re-issue the same read.
    const requestedTaskIdRef = useRef<ID | null>(null);

    useEffect(() => {
        if (!task || !isModuleEnabled) {
            return;
        }

        if (requestedTaskIdRef.current === task.id) {
            return;
        }

        requestedTaskIdRef.current = task.id;

        // The aside owns its scope, so it claims it before reading. A summary refresh triggered
        // by a mutation on the previous Request is then dropped on arrival rather than landing
        // under this one.
        claimScope('aside', task.id);

        fetchSubTicketSummary({
            filter: { task_id: task.id },
            // Aside-scoped: this read survives the Sub Tasks page unmounting on a tab switch
            generation: nextGeneration(summaryKey(task.id)),
        }).catch((e) => {
            console.error(e);
        });
    }, [task?.id, isModuleEnabled]);

    // This component owns the aside scope, so it — and only it — invalidates it. Teardown of the
    // Sub Tasks Outlet must leave an in-flight summary alone, but a real unmount of the Aside
    // (navigating off the Request entirely) must drop a late Task A reply so it cannot land
    // under Task B.
    useEffect(() => {
        return () => {
            requestedTaskIdRef.current = null;
            invalidateScope('aside');
            releaseScope('aside');
            resetSubTicketSummary();
        };
    }, []);

    // Switching Requests without unmounting: invalidate the previous task's read before issuing
    // the next one, so A's reply cannot repopulate the store while B is on screen
    useEffect(() => {
        return () => {
            invalidateScope('aside');
        };
    }, [task?.id]);

    if (!isModuleEnabled || !summary || !task) {
        return null;
    }

    // Belt and braces with the generation guard: even if a reply somehow lands out of turn, a
    // summary belonging to a different Request is never rendered under this one
    if (summary.task_id !== task.id) {
        return null;
    }

    // A Request without sub-tasks shows nothing at all: an empty card in the aside of every
    // ordinary Request would be pure noise
    if (summary.total === 0) {
        return null;
    }

    const renderStatusSummary = () => (
        <Flex direction="row" align="center" style={{ flexWrap: 'wrap' }}>
            {STATUS_ORDER.map((status) => (
                <Tag className="mr-1 mt-1" key={status} style={getSubTicketStatusTagStyle(status)}>
                    {taskSubTicketStatusInformation[status].title}: {summary.status_counts[status]}
                </Tag>
            ))}
        </Flex>
    );

    // One row per ACTIVE assignment, not one per designer. A designer holding two phases in two
    // different statuses is exactly what the previous avatar stack could not say: it collapsed
    // them into a single avatar, so the aside reported presence and nothing else.
    //
    // Each row therefore names the three facts that make it unambiguous — who, which immutable
    // `#ordinal` label, and that phase's own status.
    const renderAssignments = () => {
        const assignments = resolveAssigneeAssignments(summary.assignments ?? []);

        if (assignments.length === 0) {
            return null;
        }

        return (
            <div className="mt-2">
                <DevText muted className="mb-1">
                    Designers
                </DevText>

                {assignments.map((assignment) => {
                    const { assignee_user: user } = assignment;

                    const avatarSrc =
                        user.image_hash ? imageHashPreview.userImage(user.image_hash, ImageHashPreviewSize.XS) : null;

                    const fullName = `${user.first_name} ${user.last_name}`;

                    return (
                        <Flex
                            key={assignment.sub_ticket_id}
                            className="mt-1"
                            direction="row"
                            align="center"
                            justify="space-between"
                        >
                            <Flex className="mr-1" direction="row" align="center">
                                <Avatar className="mr-1" width="20px" height="20px" alt={fullName} src={avatarSrc} />
                                <DevText>{fullName}</DevText>
                            </Flex>

                            <Flex direction="row" align="center">
                                <DevText muted small className="mr-1">
                                    Sub-task #{assignment.ordinal}
                                </DevText>
                                <Tag style={getSubTicketStatusTagStyle(assignment.status)}>
                                    {taskSubTicketStatusInformation[assignment.status].title}
                                </Tag>
                            </Flex>
                        </Flex>
                    );
                })}
            </div>
        );
    };

    return (
        <Card {...props}>
            <Flex className="mb-1" align="center" justify="space-between">
                <Flex direction="row" align="center">
                    <Heading className="mr-1" type="h4">
                        Sub Tasks
                    </Heading>
                    <Tag minimal round>
                        {summary.total}
                    </Tag>
                </Flex>
            </Flex>

            {renderStatusSummary()}

            <Flex className="mt-2" direction="row" align="center" justify="space-between">
                <DevText muted>Total logged</DevText>
                <DevText>{formatLoggedMinutes(summary.total_logged_min)}</DevText>
            </Flex>

            {renderAssignments()}

            <Flex className="mt-2" justify="flex-end">
                {/* The aside sits above a long Request page, so landing on the Sub Tasks tab is
                    not the same as seeing the list. The intent travels as location state and the
                    page acts on it; a repeat click re-navigates, so it works from the tab too. */}
                <RouterLink
                    color={Colors.WHITE}
                    to={Endpoints.TASK_SUB_TICKETS.replace(':taskId', task.id)}
                    state={{ scrollToSubTickets: true }}
                >
                    View all →
                </RouterLink>
            </Flex>
        </Card>
    );
};

export default SubTicketSummary;
